import { Injectable } from '@angular/core';
import { HttpContext, HttpContextToken } from '@angular/common/http';
import {
  NbAuthResult,
  NbAuthStrategyClass,
  NbPasswordAuthStrategy,
  NbPasswordAuthStrategyOptions,
} from '@nebular/auth';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/** Marks requests that must go out as-is: no token check/refresh and no Authorization header. */
export const SKIP_TOKEN_REFRESH = new HttpContextToken<boolean>(() => false);

const LOGIN_MUTATION = `mutation Login($input: LoginInput!) {
  login(input: $input) {
    accessToken
    refreshToken
  }
}`;

/**
 * Password strategy where login is the Hasura `login` action (public, guest role).
 * The refresh token arrives only as an HttpOnly cookie, never from the body.
 * Refresh and logout go to the auth backend next to Hasura (`auth_path`), the site that set the cookie.
 */
@Injectable()
export class ExpofinAuthStrategy extends NbPasswordAuthStrategy {

  static override setup(options: NbPasswordAuthStrategyOptions): [NbAuthStrategyClass, NbPasswordAuthStrategyOptions] {
    return [ExpofinAuthStrategy, options];
  }

  override authenticate(data?: any): Observable<NbAuthResult> {
    const module = 'login';
    const input = { username: data?.username ?? data?.email, password: data?.password, origin: 'web' };
    return this.http.post<any>(
      environment.hasura_uri,
      { operationName: 'Login', query: LOGIN_MUTATION, variables: { input } },
      { observe: 'response', withCredentials: true, context: new HttpContext().set(SKIP_TOKEN_REFRESH, true) },
    ).pipe(
      map(res => {
        // Hasura reports action errors as HTTP 200 + errors[].
        const error = res.body?.errors?.[0]?.message;
        const accessToken = res.body?.data?.login?.accessToken;
        if (error || !accessToken) {
          return new NbAuthResult(false, res, this.getOption(`${module}.redirect.failure`), [error || 'Accesso non riuscito.']);
        }
        return new NbAuthResult(
          true,
          res,
          this.getOption(`${module}.redirect.success`),
          [],
          this.getOption(`${module}.defaultMessages`),
          this.createToken(accessToken, this.getOption(`${module}.requireValidToken`)),
        );
      }),
      catchError(res => this.handleResponseError(res, module)),
    );
  }

  override getActionEndpoint(action: string): string {
    const endpoint = this.getOption(`${action}.endpoint`);
    if (endpoint && ['refreshToken', 'logout'].includes(action)) return environment.auth_path + endpoint;
    return super.getActionEndpoint(action);
  }
}
